import { writable, derived } from 'svelte/store';

// Track if auth has been initialized
export const authInitialized = writable(false);
export const authLoading = writable(false);

// Lazy auth instance (null until initialized)
let authInstance = null;
let authUnsubscribe = null;

function createAuthStore() {
  const { subscribe, set, update } = writable(null);

  return {
    subscribe,
    set,
    update,
    // Initialize auth only when needed
    async init() {
      if (authInstance) return; // Already initialized

      authLoading.set(true);

      try {
        // Dynamic import of Firebase Auth (NOT imported at module level!)
        const [firebaseModule, authModule] = await Promise.all([
          import('../lib/firebase.js'),
          import('firebase/auth')
        ]);

        authInstance = firebaseModule.auth;
        const onAuthStateChanged = authModule.onAuthStateChanged;

        // Listen to Firebase auth state
        authUnsubscribe = onAuthStateChanged(authInstance, (user) => {
          set(user);
          authInitialized.set(true);
          authLoading.set(false);
        });
      } catch (error) {
        console.error('Auth initialization error:', error);
        authInitialized.set(true); // Mark as initialized even on error
        authLoading.set(false);
      }
    }
  };
}

export const user = createAuthStore();
export const isAuthenticated = derived(user, $user => !!$user);
export const userEmail = derived(user, $user => $user?.email || '');

// DO NOT initialize auth automatically
// Auth will be initialized only when needed (on auth/protected routes)

// Sign out function
export async function signOut() {
  try {
    if (!authInstance) {
      await user.init();
    }

    // Dynamic import of signOut function
    const authModule = await import('firebase/auth');
    await authModule.signOut(authInstance);
    window.location.href = '/';
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

// Ensure auth is initialized (for protected routes)
export async function ensureAuth() {
  if (!authInstance) {
    await user.init();
  }

  // Wait for auth to initialize
  return new Promise((resolve) => {
    const unsubscribe = authInitialized.subscribe(initialized => {
      if (initialized) {
        unsubscribe();
        resolve();
      }
    });
  });
}
